import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, Briefcase, User, FileText, Calculator } from 'lucide-react';
import { CreatorPortalProvider, useCreatorPortal } from '../contexts/CreatorPortalContext';
import { useLanguage } from '../contexts/LanguageContext';
import { CreatorsHubHeader } from '../components/creatorshub/CreatorsHubHeader';
import { CreatorsHubKPIs } from '../components/creatorshub/CreatorsHubKPIs';
import { CreatorCampaignBoard } from '../components/creatorshub/CreatorCampaignBoard';
import { CampaignDetailDrawer } from '../components/creatorshub/CampaignDetailDrawer';
import { CreatorProfileTab } from '../components/creatorshub/CreatorProfileTab';
import { CreatorBillingView } from '../components/creatorshub/CreatorBillingView';
import { CreatorTaxPlanner } from '../components/creatorshub/CreatorTaxPlanner';

function PortalContent() {
  const { t } = useLanguage();
  const { loading, error, loadData, selectedCampaignId } = useCreatorPortal();
  const [activeTab, setActiveTab] = useState<'campaigns' | 'billing' | 'tax' | 'profile'>('campaigns');

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-paper flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500 font-medium">{t('creatorsHub.connecting')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-brand-paper flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-8 max-w-md w-full text-center">
          <p className="text-red-600 mb-4 text-sm">{error}</p>
          <button
            onClick={() => loadData()}
            className="px-5 py-2.5 bg-accent-600 text-white rounded-xl hover:bg-accent-700 text-sm font-medium transition-colors"
          >
            {t('creatorsHub.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-paper">
      <CreatorsHubHeader />

      <div className="bg-white border-b border-gray-200">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-6 overflow-x-auto scrollbar-thin" aria-label="Tabs">
            {([
              { key: 'campaigns' as const, icon: Briefcase, label: t('creatorsHub.campaigns') },
              { key: 'billing' as const, icon: FileText, label: t('creatorsHub.billing') },
              { key: 'tax' as const, icon: Calculator, label: t('creatorsHub.tax') },
              { key: 'profile' as const, icon: User, label: t('creatorsHub.profile') },
            ]).map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`relative py-3 px-1 text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                  activeTab === tab.key
                    ? 'text-brand-ink font-semibold'
                    : 'text-gray-500 hover:text-brand-ink'
                }`}
              >
                <div className="flex items-center gap-2">
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </div>
                {activeTab === tab.key && (
                  <motion.span
                    layoutId="portal-tab-underline"
                    className="absolute bottom-0 left-0 right-0 h-1 rounded-full bg-brand-lime"
                    transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                  />
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
            className="space-y-6"
          >
            {activeTab === 'campaigns' && (
              <>
                <CreatorsHubKPIs />
                <CreatorCampaignBoard />
              </>
            )}
            {activeTab === 'billing' && <CreatorBillingView />}
            {activeTab === 'tax' && <CreatorTaxPlanner />}
            {activeTab === 'profile' && <CreatorProfileTab />}
          </motion.div>
        </AnimatePresence>
      </main>

      {selectedCampaignId && <CampaignDetailDrawer />}
    </div>
  );
}

export default function InfluencerPortal() {
  return (
    <CreatorPortalProvider>
      <PortalContent />
    </CreatorPortalProvider>
  );
}
